import { Pic } from "./Pic";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { PRODUCT_DETAILS } from "@/data/productDetails";
import { SocialShare } from "./Social";
import { useT } from "@/i18n";

const Arrow = ({ dir, onClick, label }) => (
  <button onClick={onClick} aria-label={label} className={`absolute top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-[#121110]/70 text-[#F7F5F0] backdrop-blur transition-colors duration-200 hover:bg-[#C26D38] ${dir === "prev" ? "left-3" : "right-3"}`} data-testid={`gallery-${dir}`}>
    {dir === "prev" ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

const Photos = ({ photos, alt, t }) => {
  const [i, setI] = useState(0);
  const go = (d) => setI((v) => (v + d + photos.length) % photos.length);
  return (
    <div className="min-w-0">
      <div className="relative aspect-[3/2] overflow-hidden bg-[#1B1816]">
        <Pic key={photos[i]} src={photos[i]} alt={`${alt} ${i + 1}`} className="h-full w-full object-cover" data-testid="gallery-main-img" />
        {photos.length > 1 && <>
          <Arrow dir="prev" onClick={() => go(-1)} label={t("gal.prev", "Foto precedente")} />
          <Arrow dir="next" onClick={() => go(1)} label={t("gal.next", "Foto successiva")} />
          <span className="absolute bottom-3 right-3 rounded-full bg-[#121110]/70 px-3 py-1 font-mono text-xs text-[#F7F5F0]" data-testid="gallery-counter">{i + 1} / {photos.length}</span>
        </>}
      </div>
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {photos.map((src, k) => (
            <button key={src} onClick={() => setI(k)} className={`h-14 w-20 shrink-0 overflow-hidden border-2 transition-opacity duration-200 ${k === i ? "border-[#D98A52]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`gallery-thumb-${k}`} aria-label={`${alt} ${k + 1}`}>
              <Pic src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Specs = ({ id, specs, t }) => (
  <dl className="mt-6 divide-y divide-white/10 border-y border-white/10" data-testid="gallery-specs">
    {specs.map(([k, label, value]) => (
      <div key={k} className="grid gap-1 py-3 sm:grid-cols-5 sm:gap-4">
        <dt className="font-mono text-xs uppercase tracking-[0.18em] text-[#D98A52] sm:col-span-2">{t(`gal.${id}.${k}L`, label)}</dt>
        <dd className="text-sm text-[#F7F5F0] sm:col-span-3">{t(`gal.${id}.${k}`, value)}</dd>
      </div>
    ))}
  </dl>
);

const WaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" /></svg>
);

const Share = ({ p, t }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/?prodotto=${p.id}#prodotti`;
  const name = t(`prod.${p.id}.code`, p.code);
  const text = `${name} — EMMA INDUSTRIE: ${t("gal.shareText", "foto e scheda tecnica")} ${url}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("gal.copied", "Link copiato"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(url);
    }
  };
  return (
    <div className="mt-6 flex min-w-0 flex-wrap items-center gap-3 border-t border-white/10 pt-5" data-testid="gallery-share">
      <span className="font-mono text-xs uppercase tracking-[0.18em] text-[#A39E93]">{t("gal.share", "Condividi")}</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-[#0B1F13] transition-transform duration-200 hover:-translate-y-0.5" data-testid="gallery-share-whatsapp">
        <WaIcon /> WhatsApp
      </a>
      <SocialShare url={url} text={`${name} — EMMA INDUSTRIE`} t={t} btn="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-[#F7F5F0] transition-colors duration-200 hover:border-[#D98A52]" id="gallery" />
      <button onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-[#F7F5F0] transition-colors duration-200 hover:border-[#D98A52]" data-testid="gallery-share-copy">
        {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {t("gal.copy", "Copia link")}
      </button>
    </div>
  );
};

const Article = ({ id, a, t }) => (
  <details className="group mt-6 border border-white/10 bg-[#1B1816] open:bg-[#1B1816]" data-testid="gallery-article">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 font-display text-lg font-bold text-[#F7F5F0] sm:text-xl" data-testid="gallery-article-toggle">
      {t(`art.${id}.title`, a.title)}
      <ChevronRight className="h-5 w-5 shrink-0 text-[#D98A52] transition-transform duration-300 group-open:rotate-90" />
    </summary>
    <div className="px-5 pb-6">
      <p className="text-[15px] leading-relaxed text-[#E4DED3]">{t(`art.${id}.intro`, a.intro)}</p>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {a.blocks.map(([k, h, p]) => (
          <section key={k} data-testid={`gallery-article-${k}`}>
            <h4 className="font-mono text-xs uppercase tracking-[0.18em] text-[#D98A52]">{t(`art.${id}.${k}H`, h)}</h4>
            <p className="mt-2 text-[15px] leading-relaxed text-[#CFC8BC]">{t(`art.${id}.${k}`, p)}</p>
          </section>
        ))}
      </div>
    </div>
  </details>
);

export const ProductGallery = ({ product, onClose, onQuote }) => {
  const t = useT();
  const [current, setCurrent] = useState(product);
  useEffect(() => { if (product) setCurrent(product); }, [product]);
  const p = current;
  const d = p ? PRODUCT_DETAILS[p.id] : null;
  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92dvh] w-[calc(100%-1.5rem)] overflow-y-auto overflow-x-hidden border-white/10 bg-[#121110] p-4 text-[#F7F5F0] sm:max-w-4xl sm:p-6" data-testid="product-gallery">
        {p && d && <>
          <Photos key={p.id} photos={d.photos} alt={t(`prod.${p.id}.code`, p.code)} t={t} />
          <DialogTitle className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="gallery-title">{t(`prod.${p.id}.code`, p.code)}</DialogTitle>
          <DialogDescription className="mt-2 text-base leading-relaxed text-[#CFC8BC]">{t(`prod.${p.id}.desc`, p.desc)}</DialogDescription>
          <Specs id={p.id} specs={d.specs} t={t} />
          {d.article && <Article id={p.id} a={d.article} t={t} />}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button onClick={() => { onClose(); setTimeout(() => onQuote(p.type), 300); }} className="btn-amber" data-testid="gallery-quote">{t("prod.quote", "Richiedi preventivo")} <ArrowUpRight className="h-4 w-4" /></button>
            {d.page && <Link to={d.page} onClick={onClose} className="btn-ghost" data-testid="gallery-page-link">{t(d.pageLabel?.[0] ?? "gal.more", d.pageLabel?.[1] ?? "Schede PDF e calcolo")} <ArrowUpRight className="h-4 w-4" /></Link>}
          </div>
          <Share p={p} t={t} />
        </>}
      </DialogContent>
    </Dialog>
  );
};
